/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as Msdyn365 from "@msdyn365-commerce/core";

export interface DimensionValue {
  RecordId: any;
  Value: string;
  DimensionId: string;
  ColorHexCode?: any;
  ImageUrl?: any;
  RefinerGroup?: any;
  ExtensionProperties: any[];
}

export interface Dimension {
  DimensionTypeValue: number;
  DimensionValue: DimensionValue;
  ExtensionProperties: any[];
}

export interface Behavior {
  HasSerialNumber: boolean;
  IsDiscountAllowed: boolean;
  IsManualDiscountAllowed: boolean;
  IsKitDisassemblyAllowed: boolean;
  IsNegativeQuantityAllowed: boolean;
  IsReturnAllowed: boolean;
  IsSaleAtPhysicalStoresAllowed: boolean;
  IsZeroSalePriceAllowed: boolean;
  KeyInPriceValue: number;
  KeyInQuantityValue: number;
  MustKeyInComment: boolean;
  MustPrintIndividualShelfLabelsForVariants: boolean;
  MustPromptForSerialNumberOnlyAtSale: boolean;
  MustWeighProductAtSale: boolean;
  ValidFromDateForSaleAtPhysicalStores: Date;
  ValidToDateForSaleAtPhysicalStores: Date;
  IsStorageDimensionGroupLocationActive: boolean;
  IsStorageDimensionGroupLocationAllowBlankReceiptEnabled: boolean;
  AllowNegativePhysicalInventory: boolean;
  IsBlankSerialNumberAllowed: boolean;
  IsBlankSerialNumberReceiptAllowed: boolean;
  IsSerialNumberControlEnabled: boolean;
  IsStorageDimensionGroupLocationBlankIssueAllowed: boolean;
  IsSerialNumberRequired: boolean;
  DefaultQuantity: number;
  MaximumQuantity: number;
  MinimumQuantity: number;
  MultipleOfQuantity: number;
  InventoryLocationId: string;
  ExtensionProperties: any[];
}

export interface Value {
  RecordId: any;
  ItemId: string;
  Name: string;
  Description: string;
  ProductTypeValue: number;
  DefaultUnitOfMeasure: string;
  BasePrice: number;
  Price: number;
  AdjustedPrice: number;
  MasterProductId: any;
  Components: any[];
  IsGiftCard: boolean;
  Dimensions: Dimension[];
  Behavior: Behavior;
  LinkedProducts: any[];
  PrimaryImageUrl: string;
  ItemTypeValue: number;
  ItemServiceTypeValue: number;
  ExtensionProperties: any[];
}

export interface DimenstionsRootObject {
  value: Value[];
}

/**
 * BritaxGetDimensions Input Action
 */

var productId: any;
export class BritaxGetDimensionsInput implements Msdyn365.IActionInput {
  // TODO: Construct the input needed to run the action
  constructor(product: any) {
    productId = product;
  }

  // TODO: Determine if the results of this get action should cache the results and if so provide
  // a cache object type and an appropriate cache key
  public getCacheKey = () => `dimensionofproduct`;
  public getCacheObjectType = () => "dimensionofproduct";
  public dataCacheType = (): Msdyn365.CacheType => "none";
}

// TODO: Create a data model here or import one to capture the response of the action
export interface IBritaxGetDimensionsData {
  text: string;
}

/**
 * TODO: Use this function to create the input required to make the action call
 */
const createInput = (
  args: Msdyn365.ICreateActionContext
): Msdyn365.IActionInput => {
  return new BritaxGetDimensionsInput("");
};

/**
 * TODO: Use this function to call your action and process the results as needed
 */
async function action(
  input: BritaxGetDimensionsInput,
  ctx: Msdyn365.IActionContext
): Promise<DimenstionsRootObject> {
  // const apiSettings = Msdyn365.msdyn365Commerce.apiSettings;
  var data = JSON.stringify({
    channelId: ctx.requestContext.apiSettings.channelId,
    matchingDimensionValues: [],
  });
  // TODO: Uncomment the below line to get the value from a service
  const response = await Msdyn365.sendRequest<DimenstionsRootObject>(
    ctx.requestContext.apiSettings.baseUrl +
      "/Commerce/Products(" +
      productId +
      ")/GetVariantsByDimensionValues?$top=1000&api-version=7.3",
    "post",
    data,
    {
      headers: {
        "accept-language": ctx.requestContext.locale,
        authorization: "id_token " + ctx.requestContext.user.token,
        contenttype: "application/json",
        oun: ctx.requestContext.apiSettings.oun,
        "content-type": "application/json",
        accept: "application/json",
        "sec-fetch-site": "same-site",
        "sec-fetch-mode": "cors",
        "sec-fetch-dest": "empty",
      },
    }
  );
  return response.data;
}

export default Msdyn365.createObservableDataAction({
  action: <Msdyn365.IAction<DimenstionsRootObject>>action,
  // TODO: Give your data action a unique id
  id: "BritaxGetDimensions",
  input: createInput,
  // TODO: Uncomment the below line if this is a meant to be a batched data action
  // isBatched: true
});
